import { Icon, IconProps, Typography } from "@/components/atoms";
import { clsx } from "@/utils";

type NoticeTone = "warning" | "info";

/**
 * `tile` sits among the other blocks on the Payment Methods card, so it stays quiet: neutral text
 * and the glyph in a coloured tile, matching its siblings. `feature` is the whole content of a
 * dialog, so it carries the tone's own colour and more room.
 */
type NoticeVariant = "tile" | "feature";

interface GuidanceNoticeProps {
  tone: NoticeTone;
  /** the glyph for the notice — custom SVGs need isCustom, same as Icon itself */
  icon: Pick<IconProps, "name" | "variant" | "isCustom">;
  title: string;
  variant?: NoticeVariant;
  children: React.ReactNode;
}

const toneClasses: Record<NoticeTone, { panel: string; tile: string; icon: string; text: string }> = {
  warning: { panel: "bg-warning-light", tile: "bg-warning", icon: "text-warning", text: "text-warning-dark" },
  info: { panel: "bg-info-light", tile: "bg-info", icon: "text-info", text: "text-info-dark" },
};

/**
 * The guidance panel used above the card list and inside the add-card dialog (Figma 5379): a glyph
 * beside a heading and its explanation. Shared so "Good to know before you book", "Replacing a
 * card?" and the add-card warning stay identical apart from tone.
 */
export const GuidanceNotice = ({ tone, icon, title, variant = "tile", children }: GuidanceNoticeProps) => {
  const { panel, tile, icon: iconColor, text } = toneClasses[tone];
  const isFeature = variant === "feature";

  return (
    <div
      className={clsx(
        "flex rounded-lg",
        panel,
        // tile stacks on the narrowest widths; feature keeps one row — its glyph has no tile to crowd the text
        isFeature ? "items-start gap-3 p-4" : "flex-col gap-1 p-2.5 sm:flex-row sm:items-start sm:gap-2.5"
      )}
    >
      {isFeature ? (
        <Icon {...icon} size={20} className={clsx("mt-0.5 shrink-0", iconColor)} />
      ) : (
        <div className={clsx("flex h-8 w-8 shrink-0 items-center justify-center rounded", tile)}>
          <Icon {...icon} size={18} className="text-white" />
        </div>
      )}
      <div className="flex flex-col">
        <Typography variant="small" className={isFeature ? text : "text-neutral-dark-gray"}>
          {title}
        </Typography>
        <Typography variant="small-regular" className={clsx("!font-medium", isFeature ? text : "text-neutral-dark-gray")}>
          {children}
        </Typography>
      </div>
    </div>
  );
};
